import type {
  CalculationMode,
  CalculationResult,
  AmortizationSummary,
  ScheduleRow,
  YearlyScheduleGroup,
} from './types';

// Formatters
export const formatCurrency = (val: number, decimals: number = 2) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(val);
};

export const formatShortCurrency = (val: number) => {
  if (val >= 10000000) {
    return `₹ ${(val / 10000000).toFixed(2)} Cr`;
  }
  if (val >= 100000) {
    return `₹ ${(val / 100000).toFixed(2)} Lakh`;
  }
  if (val >= 1000) {
    return `₹ ${(val / 1000).toFixed(0)}k`;
  }
  return `₹ ${Math.round(val)}`;
};

export const getModeTitle = (m: CalculationMode): string => {
  switch (m) {
    case 'principal':
      return 'Loan Amount';
    case 'rate':
      return 'Annual Interest Rate';
    case 'tenure':
      return 'Loan Duration';
    case 'emi':
      return 'Monthly EMI';
  }
};

// Core 4-way solver function
export function performCalculation(
  mode: CalculationMode,
  P_in: number,
  rate_in: number,
  N_in: number,
  emi_in: number,
): CalculationResult {
  let resolvedPrincipal = Math.round(P_in * 100) / 100;
  let resolvedRate = rate_in;
  let resolvedTotalMonths = Math.round(N_in);
  let resolvedEmi = Math.round(emi_in * 100) / 100;
  let error: string | null = null;

  if (mode === 'emi') {
    const P = P_in;
    const annualRate = rate_in;
    const N = N_in;

    if (P <= 0 || !isFinite(P)) {
      error = 'Please enter a valid Principal Loan Amount greater than ₹0.';
    } else if (P > 1000000000) {
      error = 'Principal loan amount exceeds maximum limit of ₹100 Crore.';
    } else if (N <= 0 || !isFinite(N)) {
      error = 'Please enter a valid Loan Duration of at least 1 month.';
    } else if (N > 600) {
      error = 'Loan duration exceeds maximum supported tenure of 50 years (600 months).';
    } else if (annualRate < 0 || !isFinite(annualRate)) {
      error = 'Annual interest rate cannot be negative.';
    } else if (annualRate > 100) {
      error = 'Annual interest rate exceeds maximum supported limit of 100% p.a.';
    } else {
      let emi = 0;
      if (annualRate === 0) {
        emi = P / N;
      } else {
        const r = annualRate / 12 / 100;
        const factor = Math.pow(1 + r, N);
        emi = (P * r * factor) / (factor - 1);
      }
      resolvedEmi = Math.round(emi * 100) / 100;
      resolvedPrincipal = Math.round(P * 100) / 100;
      resolvedRate = annualRate;
      resolvedTotalMonths = Math.round(N);
    }
  } else if (mode === 'tenure') {
    const P = P_in;
    const annualRate = rate_in;
    const emi = emi_in;

    if (P <= 0 || !isFinite(P)) {
      error = 'Please enter a valid Principal Loan Amount greater than ₹0.';
    } else if (emi <= 0 || !isFinite(emi)) {
      error = 'Please enter a valid Monthly EMI greater than ₹0.';
    } else if (annualRate < 0 || !isFinite(annualRate)) {
      error = 'Annual interest rate cannot be negative.';
    } else if (annualRate > 100) {
      error = 'Annual interest rate exceeds maximum supported limit of 100% p.a.';
    } else if (annualRate === 0) {
      const monthsRequired = Math.ceil(P / emi);
      if (monthsRequired > 600) {
        error =
          'Calculated loan duration exceeds maximum supported tenure of 50 years (600 months).';
      } else {
        resolvedTotalMonths = Math.max(1, monthsRequired);
        resolvedPrincipal = Math.round(P * 100) / 100;
        resolvedEmi = Math.round(emi * 100) / 100;
        resolvedRate = annualRate;
      }
    } else {
      const r = annualRate / 12 / 100;
      const firstMonthInterest = Math.round(P * r * 100) / 100;
      if (emi <= firstMonthInterest) {
        error = `Monthly EMI (${formatCurrency(emi)}) must be greater than monthly interest (${formatCurrency(firstMonthInterest)}) to repay the loan.`;
      } else {
        let balance = Math.round(P * 100) / 100;
        let monthsCount = 0;
        let impossible = false;

        while (balance > 0.005) {
          monthsCount++;
          if (monthsCount > 600) {
            impossible = true;
            break;
          }
          const interest = Math.round(balance * r * 100) / 100;
          if (emi <= interest) {
            impossible = true;
            break;
          }
          const regularPrincipal = Math.round((emi - interest) * 100) / 100;
          if (balance <= regularPrincipal || balance + interest <= emi + 1.0) {
            balance = 0;
            break;
          } else {
            balance = Math.round((balance - regularPrincipal) * 100) / 100;
          }
        }

        if (impossible || monthsCount > 600) {
          error =
            'Calculated loan duration exceeds maximum supported tenure of 50 years (600 months).';
        } else {
          resolvedTotalMonths = Math.max(1, monthsCount);
          resolvedPrincipal = Math.round(P * 100) / 100;
          resolvedEmi = Math.round(emi * 100) / 100;
          resolvedRate = annualRate;
        }
      }
    }
  } else if (mode === 'rate') {
    const P = P_in;
    const N = N_in;
    const emi = emi_in;

    if (P <= 0 || !isFinite(P)) {
      error = 'Please enter a valid Principal Loan Amount greater than ₹0.';
    } else if (N <= 0 || !isFinite(N)) {
      error = 'Please enter a valid Loan Duration of at least 1 month.';
    } else if (N > 600) {
      error = 'Loan duration exceeds maximum supported tenure of 50 years (600 months).';
    } else if (emi <= 0 || !isFinite(emi)) {
      error = 'Please enter a valid Monthly EMI greater than ₹0.';
    } else {
      const zeroRateEmi = P / N;
      if (emi < zeroRateEmi - 1e-4) {
        error = `Total payment (${formatCurrency(emi * N)}) is less than principal (${formatCurrency(P)}). Monthly EMI must be at least ${formatCurrency(zeroRateEmi)} for a non-negative rate.`;
      } else if (Math.abs(emi - zeroRateEmi) <= 1e-4) {
        resolvedRate = 0;
        resolvedPrincipal = Math.round(P * 100) / 100;
        resolvedEmi = Math.round(emi * 100) / 100;
        resolvedTotalMonths = Math.round(N);
      } else {
        const f = (rMonthly: number) => {
          return (P * rMonthly) / (1 - Math.pow(1 + rMonthly, -N)) - emi;
        };

        let low = 0;
        let high = 1.0;
        while (f(high) < 0 && high < 2.0) {
          high *= 2;
        }

        if (f(high) < 0) {
          error = 'Calculated interest rate exceeds supported maximum (1000% p.a.).';
        } else {
          for (let i = 0; i < 70; i++) {
            const mid = (low + high) / 2;
            const val = f(mid);
            if (Math.abs(val) < 1e-9) {
              low = mid;
              high = mid;
              break;
            }
            if (val < 0) {
              low = mid;
            } else {
              high = mid;
            }
          }
          const monthlyRate = (low + high) / 2;
          const computedAnnualRate = monthlyRate * 12 * 100;
          resolvedRate = computedAnnualRate; // Retain full precision internally!
          resolvedPrincipal = Math.round(P * 100) / 100;
          resolvedEmi = Math.round(emi * 100) / 100;
          resolvedTotalMonths = Math.round(N);
        }
      }
    }
  } else if (mode === 'principal') {
    const annualRate = rate_in;
    const N = N_in;
    const emi = emi_in;

    if (emi <= 0 || !isFinite(emi)) {
      error = 'Please enter a valid Monthly EMI greater than ₹0.';
    } else if (N <= 0 || !isFinite(N)) {
      error = 'Please enter a valid Loan Duration of at least 1 month.';
    } else if (N > 600) {
      error = 'Loan duration exceeds maximum supported tenure of 50 years (600 months).';
    } else if (annualRate < 0 || !isFinite(annualRate)) {
      error = 'Annual interest rate cannot be negative.';
    } else if (annualRate > 100) {
      error = 'Annual interest rate exceeds maximum supported limit of 100% p.a.';
    } else {
      let computedPrincipal = 0;
      if (annualRate === 0) {
        computedPrincipal = emi * N;
      } else {
        const r = annualRate / 12 / 100;
        computedPrincipal = (emi * (1 - Math.pow(1 + r, -N))) / r;
      }

      if (!isFinite(computedPrincipal) || computedPrincipal <= 0) {
        error = 'Unable to calculate a valid principal amount.';
      } else if (computedPrincipal > 1000000000) {
        error = 'Calculated principal loan amount exceeds maximum limit of ₹100 Crore.';
      } else {
        const roundedInt = Math.round(computedPrincipal);
        // Smart snapping: If calculated principal is within 15 paise of a whole rupee, snap to clean rupee
        resolvedPrincipal =
          Math.abs(computedPrincipal - roundedInt) < 0.15
            ? roundedInt
            : Math.round(computedPrincipal * 100) / 100;
        resolvedEmi = Math.round(emi * 100) / 100;
        resolvedRate = annualRate;
        resolvedTotalMonths = Math.round(N);
      }
    }
  }

  const resolvedYears = Math.floor(resolvedTotalMonths / 12);
  const resolvedMonths = resolvedTotalMonths % 12;

  return {
    mode,
    resolvedPrincipal,
    resolvedRate,
    resolvedTotalMonths,
    resolvedYears,
    resolvedMonths,
    resolvedEmi,
    error,
    isCalculated: true,
  };
}

export function computeAmortizationSchedule(
  P: number,
  annualRate: number,
  N: number,
  emi: number,
): AmortizationSummary {
  if (P <= 0 || N <= 0 || emi <= 0) {
    return {
      emi: 0,
      totalPrincipal: P > 0 ? P : 0,
      totalInterest: 0,
      totalAmount: P > 0 ? P : 0,
      interestRatio: 0,
      schedule: [],
      finalMonthEmi: 0,
      hasFinalAdjustment: false,
    };
  }

  const r = annualRate / 12 / 100;
  let currentBalance = Math.round(P * 100) / 100;
  const schedule: ScheduleRow[] = [];

  for (let m = 1; m <= N; m++) {
    if (currentBalance <= 0) {
      break;
    }

    const interestForMonth = Math.round(currentBalance * r * 100) / 100;
    let principalForMonth: number;
    let emiPaid: number;
    let closingBalance: number;

    if (m === N || currentBalance + interestForMonth <= emi) {
      principalForMonth = currentBalance;
      emiPaid = Math.round((principalForMonth + interestForMonth) * 100) / 100;
      closingBalance = 0;
    } else {
      principalForMonth = Math.round((emi - interestForMonth) * 100) / 100;
      emiPaid = emi;
      closingBalance = Math.round(Math.max(0, currentBalance - principalForMonth) * 100) / 100;
    }

    schedule.push({
      monthNumber: m,
      yearNumber: Math.ceil(m / 12),
      monthInYear: ((m - 1) % 12) + 1,
      openingBalance: currentBalance,
      emiPaid,
      principalPaid: principalForMonth,
      interestPaid: interestForMonth,
      closingBalance,
    });

    currentBalance = closingBalance;
  }

  const totalInterest =
    Math.round(schedule.reduce((sum, item) => sum + item.interestPaid, 0) * 100) / 100;
  const totalPrincipal =
    Math.round(schedule.reduce((sum, item) => sum + item.principalPaid, 0) * 100) / 100;
  const totalAmount = Math.round(schedule.reduce((sum, item) => sum + item.emiPaid, 0) * 100) / 100;
  const interestRatio = totalAmount > 0 ? (totalInterest / totalAmount) * 100 : 0;
  const finalMonthEmi = schedule.length > 0 ? schedule[schedule.length - 1].emiPaid : emi;
  const hasFinalAdjustment = Math.abs(finalMonthEmi - emi) > 0.01;

  return {
    emi,
    totalPrincipal,
    totalInterest,
    totalAmount,
    interestRatio,
    schedule,
    finalMonthEmi,
    hasFinalAdjustment,
  };
}

export function groupScheduleByYear(schedule: ScheduleRow[]): YearlyScheduleGroup[] {
  const yearsMap: Record<number, YearlyScheduleGroup> = {};

  schedule.forEach((row) => {
    const y = row.yearNumber;
    if (!yearsMap[y]) {
      yearsMap[y] = {
        yearNumber: y,
        openingBalance: row.openingBalance,
        principalPaid: 0,
        interestPaid: 0,
        totalEmi: 0,
        closingBalance: 0,
        months: [],
      };
    }
    yearsMap[y].principalPaid =
      Math.round((yearsMap[y].principalPaid + row.principalPaid) * 100) / 100;
    yearsMap[y].interestPaid =
      Math.round((yearsMap[y].interestPaid + row.interestPaid) * 100) / 100;
    yearsMap[y].totalEmi = Math.round((yearsMap[y].totalEmi + row.emiPaid) * 100) / 100;
    yearsMap[y].closingBalance = row.closingBalance;
    yearsMap[y].months.push(row);
  });

  return Object.values(yearsMap);
}
